
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface LogEntry {
  id: string;
  timestamp: string;
  type: 'petition' | 'news' | 'info' | 'error';
  message: string;
  details?: string;
  success: boolean;
}

interface ActivityLogProps {
  logs: LogEntry[];
}

export default function ActivityLog({ logs }: ActivityLogProps) {
  const [filter, setFilter] = useState<'all' | 'petition' | 'news' | 'error'>('all');

  const filteredLogs = logs.filter((log) => {
    if (filter === 'all') return true;
    if (filter === 'error') return !log.success;
    return log.type === filter;
  });

  const getTypeIcon = (type: string, success: boolean) => {
    if (!success) return { icon: 'ri-error-warning-line', color: 'text-red-500', bg: 'bg-red-50' };
    switch (type) {
      case 'petition':
        return { icon: 'ri-file-list-3-line', color: 'text-amber-600', bg: 'bg-amber-50' };
      case 'news':
        return { icon: 'ri-newspaper-line', color: 'text-teal-600', bg: 'bg-teal-50' };
      default:
        return { icon: 'ri-information-line', color: 'text-gray-500', bg: 'bg-gray-50' };
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const formatFullTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const filters = [
    { key: 'all' as const, label: '전체' },
    { key: 'petition' as const, label: '청원' },
    { key: 'news' as const, label: '뉴스' },
    { key: 'error' as const, label: '오류' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50">
              <i className="ri-history-line text-lg text-gray-600"></i>
            </div>
            <h2 className="text-sm font-bold text-gray-900">실행 기록</h2>
          </div>
          <span className="text-xs text-gray-400">{filteredLogs.length}건</span>
        </div>
        <div className="flex gap-1.5">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === f.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[480px] overflow-y-auto">
        {filteredLogs.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-gray-50">
              <i className="ri-inbox-line text-2xl text-gray-300"></i>
            </div>
            <p className="text-sm text-gray-400">실행 기록이 없습니다</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {filteredLogs.map((log) => {
              const typeStyle = getTypeIcon(log.type, log.success);
              return (
                <div key={log.id} className="px-5 py-3.5 hover:bg-gray-50/50 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${typeStyle.bg} flex-shrink-0 mt-0.5`}>
                      <i className={`${typeStyle.icon} text-base ${typeStyle.color}`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-gray-800">{log.message}</span>
                        <div
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            log.success ? 'bg-emerald-400' : 'bg-red-400'
                          }`}
                        ></div>
                      </div>
                      {log.details && (
                        <p className="text-xs text-gray-400 truncate">{log.details}</p>
                      )}
                      <p className="text-xs text-gray-300 mt-1" title={formatFullTime(log.timestamp)}>
                        {formatTime(log.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export type { LogEntry };
